import { assertNever } from "@ondo/shared";
import { Link, Navigate, useParams } from "react-router";

import { AgentWorkspace } from "../agent/AgentWorkspace.tsx";
import { GalleryWorkspace } from "../gallery/GalleryWorkspace.tsx";
import type { AgentDestination } from "../agent/agent.constants.ts";
import type { GalleryDestination } from "../gallery/gallery.constants.ts";
import { CollectorWorkspace } from "../collector/CollectorWorkspace.tsx";
import type { CollectorDestination } from "../collector/collector.model.ts";
import { EstateWorkspace } from "../estate/EstateWorkspace.tsx";
import type { EstateDestination } from "../estate/estate.constants.ts";
import { LoadingScreen } from "../lib/Loading.tsx";
import { VerifierWorkspace } from "../verifier/VerifierWorkspace.tsx";
import type { VerifierDestination } from "../verifier/verifier.constants.ts";
import { useAccount } from "../lib/account.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { WorkspaceShell } from "./WorkspaceShell.tsx";
import { resolveWorkspaceRoute } from "./workspace.model.ts";
import "./workspace.css";

/**
 * `/:role/:org` and `/:role/:org/:destination` — one open workspace.
 *
 * Flat rather than a nested layout with an outlet, because the chrome differs
 * by role: a role with a PORTAL brings the sidebar shell, and one whose screen
 * is not built yet has a single page and nothing to navigate. All three URL
 * shapes render this same component, so moving between destinations re-renders
 * the shell rather than remounting it, and the sidebar does not flash.
 *
 * The five portal branches below cast their destination, and the cast is safe
 * for a reason worth stating: `resolveWorkspaceRoute` parsed it against THIS
 * role's own spec through `parseDestination`, which returns null for anything
 * not on it. The union is erased crossing the route view — one view type cannot
 * be generic over every portal at once — and recovered here, where the role is
 * known. Each screen then switches over its own destinations exhaustively.
 *
 * Five branches rather than a `Record<screen, Component>` table, and the trade
 * is conscious: the components' props genuinely differ (the verifier takes no
 * `record`, because no destination of its takes a fourth URL segment), and a
 * table erasing that would move the difference into every component. What the
 * shape costs is that a sixth portal needs a branch added HERE as well as in
 * `portals.ts` — and forgetting it falls through to the placeholder page below,
 * visibly, which is the failure `workspaceScreen` chose too.
 */
export function WorkspaceRoute() {
  const { me, signOut } = useAccount();
  const { role, org, destination, record } = useParams();

  const view = resolveWorkspaceRoute({ me, role, org, destination, record });

  switch (view.kind) {
    case "loading":
      return (
        <WorkspaceShell onSignOut={signOut}>
          <LoadingScreen message={view.message} />
        </WorkspaceShell>
      );

    case "error":
      return (
        <WorkspaceShell onSignOut={signOut}>
          <h1 className="workspace__title">{view.title}</h1>
          <p className="workspace__note">{view.message}</p>
          {view.unreachable && (
            <p className="workspace__note">
              You are signed in — this is a connection problem, not an account
              one. If you are running ONDO locally, start it with{" "}
              <code>pnpm dev</code> from the repository root.
            </p>
          )}
        </WorkspaceShell>
      );

    case "unprovisioned":
      return (
        <WorkspaceShell onSignOut={signOut}>
          <h1 className="workspace__title">{view.title}</h1>
          <p className="workspace__note">{view.message}</p>
        </WorkspaceShell>
      );

    case "redirect":
      return <Navigate to={view.to} replace />;

    case "open":
      if (view.screen === "collector" && view.destination) {
        return (
          <CollectorWorkspace
            destination={view.destination as CollectorDestination}
            record={view.record}
            workspace={view.workspace}
            principal={view.principal}
            capabilities={view.capabilities}
            membership={view.membership}
            canSwitch={view.canSwitch}
            onSignOut={signOut}
          />
        );
      }

      if (view.screen === "agent" && view.destination) {
        return (
          <AgentWorkspace
            destination={view.destination as AgentDestination}
            record={view.record}
            workspace={view.workspace}
            principal={view.principal}
            capabilities={view.capabilities}
            membership={view.membership}
            canSwitch={view.canSwitch}
            onSignOut={signOut}
          />
        );
      }

      if (view.screen === "gallery" && view.destination) {
        return (
          <GalleryWorkspace
            destination={view.destination as GalleryDestination}
            record={view.record}
            workspace={view.workspace}
            principal={view.principal}
            capabilities={view.capabilities}
            membership={view.membership}
            canSwitch={view.canSwitch}
            onSignOut={signOut}
          />
        );
      }

      if (view.screen === "estate" && view.destination) {
        return (
          <EstateWorkspace
            destination={view.destination as EstateDestination}
            record={view.record}
            workspace={view.workspace}
            principal={view.principal}
            capabilities={view.capabilities}
            membership={view.membership}
            canSwitch={view.canSwitch}
            onSignOut={signOut}
          />
        );
      }

      // No `record` passed, unlike the four above. Every destination in the
      // verification portal is `records: "none"`, so `view.record` is always
      // null here — handing it over would suggest a fourth URL segment means
      // something in this portal, and it does not.
      if (view.screen === "verifier" && view.destination) {
        return (
          <VerifierWorkspace
            destination={view.destination as VerifierDestination}
            workspace={view.workspace}
            principal={view.principal}
            capabilities={view.capabilities}
            membership={view.membership}
            canSwitch={view.canSwitch}
            onSignOut={signOut}
          />
        );
      }

      return (
        <WorkspaceShell onSignOut={signOut} principalName={view.principal.displayName}>
          <h1 className="workspace__title">{view.title}</h1>
          <p className="workspace__note">{view.meta}</p>
          {view.canSwitch && (
            <Link className="workspace__switch" to={PATHS.workspaces}>
              Switch workspace
            </Link>
          )}
        </WorkspaceShell>
      );

    default:
      return assertNever(view);
  }
}
